<template>
    <div v-if="!timer.hideTimer" class="timer-box" :class="[isAvailable ? 'available' : 'not-available']">
        <span class="corner-dot" :style="{ backgroundColor: colorDot ? colorDot.color : '#374673' }"></span>
        <div class="text-container">
            <h2 class="timer-title">{{ timer.name }}</h2>
            <p class="timer-text timer-description">{{ timer.description }}</p>
            <p v-if="isAvailable && currentPendingAlarm" class="time">
                <span v-if="timer.type == 2" class="timer-text pending">{{ $t('timers.pending_signing') }}:</span>
                <span v-else class="timer-text ">{{ $t('timers.pending') }}:</span>
                <span class="timer-text bold"> {{ displayPendingAlarm }}</span>
            </p>
            <p v-else-if="!timer.isManual" class="time">
                <span class="timer-text ongoaing">{{ $t('timers.next_alarm') }}:</span>
                <span v-show="displayNextAlarm" class="timer-text bold"> {{ displayNextAlarm }}</span>
            </p>
        </div>
        <template v-if="isAvailable">
            <div class="timer-countdown-container action" :class="['type' + timer.type]" @click="handleTimerClick"></div>
        </template>
        <template v-else>
          <template v-if="timer.isManual">
            <div class="timer-countdown-container hour">
              <div class="graph chart">
                <!-- El aro usa el % que viene del CountDownManual -->
                <div class="c100" :class="['p' + manual_pc, { small: isSmallScreen }]">
                  <div class="slice">
                    <div class="bar"  :style="{ borderColor: (colorDot ? colorDot.color : '#374673') + ' !important' }"></div>
                    <div class="fill" :style="{ borderColor: (colorDot ? colorDot.color : '#374673') + ' !important' }"></div>
                  </div>

                  <CountDownManual
                    ref="manualTimer"
                    :minutes="manualMinutes"
                    :timerId="timer.id"
                    @tick="updateManualPc"
                    @started="manualRunning = true"
                    @resumed="manualRunning = true"
                    @paused="manualRunning = false"
                    @finished="handleManualFinished"
                    @stopped="handleManualStopped"
                    @needsAction="handleManualNeedsAction"
                    @reset="handleManualReset"
                    @stateChanged="handleManualStateChange"
                  />
                </div>
              </div>
            </div>
          </template>
            <template v-else>
                <div class="timer-countdown-container hour">
                    <div class="graph chart">
                        <div class="c100" :class="['p' + pending_pc, { small: isSmallScreen }]">
                            <div class="slice">
                                <div class="bar" :style="{ borderColor: (colorDot ? colorDot.color : '#374673') + ' !important' }"></div>
                                <div class="fill" :style="{ borderColor: (colorDot ? colorDot.color : '#374673') + ' !important' }"></div>
                            </div>
                            <CountDown :nextAlarm="timer.nextAlarm" @timeStatusChanged="handleTimeStatusChange" />
                        </div>
                    </div>
                </div>
            </template>
        </template>
    </div>
</template>

<script>
import timer from '../../../store/modules/timer'
import CountDown from './CountDown.vue'
import CountDownManual from './CountDownManual.vue'
import { timerSyncService } from '@/services/timerSyncService'
import { soundService } from '@/services/soundService'
import { getLocationTimestamp } from '@/utils/locationTime'
import { formatTimerAlarmFromStore } from '@/utils/dateFormatters'

export default {
    name: 'SingleTimer',
    components: { CountDown, CountDownManual },
    props: { timerId: String, colorDot: {
      type: Object,
      // color por defecto si no viene categoría todavía
      default: () => ({ color: '#374673' })
    }, showAll: Boolean },
    data() {
        return {
            clickTimestamp: null,
            windowWidth: window.innerWidth,
            isMoreThanSixHours: true,
            manual_pc: 0,
            manualRunning: false,
            manualPct: 0,
            manualNeedsAction: false,
            uiTick: 0
        }
    },
    computed: {
        isSmallScreen() {
            return this.windowWidth < 1025
        },
        timer() {
            if (this.timerId) {
                return this.$store.getters['timer/getTimerById'](this.timerId) || null
            }
            return null
        },
        manualMinutes() {
            if (!this.timer) return 0
            // prioridad: manualMinutes -> durationMinutes -> times.iv (asumiendo viene en minutos)
            if (this.timer.manualMinutes != null) return Number(this.timer.manualMinutes)
            if (this.timer.durationMinutes != null) return Number(this.timer.durationMinutes)
            if (this.timer.times && this.timer.times.iv != null) return Number(this.timer.times.iv)
            return 0
        },
        pending_pc() {
            const self = this

            if (!self.timer || !self.timer.nextAlarm) {
                return 0 // Return 0 if timer or nextAlarm is not available
            }

            // Dependencia reactiva para recalcular el aro cada segundo (getLocationNow en Vuex se cachea).
            void self.uiTick

            const nextAlarm = moment.utc(self.timer.nextAlarm)

            let pastAlarm

            if (self.timer.previousAlarm) {
                pastAlarm = moment.utc(self.timer.previousAlarm)
            } else if (self.timer.times && self.timer.times.length > 0) {
                const todayLastAlarm = moment.utc(self.timer.times.at(-1).timestamp)

                const todayLastAlarmClone = todayLastAlarm.clone()
                const yesterdayLastAlarm = todayLastAlarmClone.subtract(1, 'days')

                pastAlarm = yesterdayLastAlarm
            } else {
                return 0 // Return 0 if we can't determine pastAlarm
            }

            const currentTimestampWithSeconds = getLocationTimestamp(self.$store.getters)
            const totalPendingTimeInSeconds = nextAlarm.diff(pastAlarm, 'seconds')
            const currentPendingTimeInSeconds = moment.utc(currentTimestampWithSeconds).diff(pastAlarm, 'seconds')

            const elapsed = (currentPendingTimeInSeconds / totalPendingTimeInSeconds) * 100

            return parseInt(elapsed)
        },
        countDownTargetTime() {
            const self = this
            const time = self.nextAlarm
            const timeParts = time.split(':')

            return {
                targetHour: timeParts[0],
                targetMinute: timeParts[1]
            }
        },
        isAvailable() {
        if (this.timer) {
            // Si es un timer manual que necesita acción, considerarlo disponible
            if (this.timer.isManual && this.manualNeedsAction) {
                return true
            }
            return this.timer.isAvailable
        }
    },
        soundEnabled() {
            return this.$store.getters['isSoundEnabled']
        },
        nextAlarm() {
            if (this.timer && this.timer.nextAlarm) {
                return formatTimerAlarmFromStore(moment.utc(this.timer.nextAlarm), { emptyValue: '' })
            }

            return null
        },
        currentPendingAlarm() {
            if (this.timer && this.timer.currentPendingAlarm) {
                return formatTimerAlarmFromStore(moment.utc(this.timer.currentPendingAlarm), { emptyValue: '' })
            }

            return null
        },
        displayPendingAlarm() {
            if (this.timer && this.timer.nextAlarmText) {
                return this.timer.nextAlarmText
            }

            return this.currentPendingAlarm
        },
        displayNextAlarm() {
            if (this.timer && this.timer.nextAlarmText) {
                return this.timer.nextAlarmText
            }

            return this.nextAlarm
        },
        userAppAccess() {
            return this.$store.getters['loginUser/getHasAccessApp']
        },
        loggedUser() {
            return this.userAccess ? this.userAccess : this.$store.getters['loginUser/getLocalEmployee']
        },
        user() {
            if (this.loggedUser) {
                return this.$store.getters['employee/getEmployee'](this.loggedUser)
            }
            return false
        },

    },
    watch: {
        /**
         * Watcher para detectar cuando el timer está disponible (caducado)
         */
        // Cubre tanto timers normales (timer.isAvailable) como manuales
        // (isManual && manualNeedsAction), ya que isAvailable depende de ambos.
        // No se necesita un watcher separado para manualNeedsAction: hacerlo
        // dispararía el sonido dos veces con el mismo timerId al mismo tick.
        isAvailable(newValue, oldValue) {
            if (newValue && !oldValue && this.timer && this.soundEnabled) {
                soundService.playNotificationSound('/sounds/timer-expired-alert.mp3', 1.0, `operational-timer-${this.timer.id}`)
            }
        },

        /**
         * Watcher para el timer completo
         */
        timer: {
            handler(newTimer) {
                if (newTimer) {
                    this.checkTimerExpiration()
                }
            },
            deep: true
        }
    },
    methods: {
      /**
       * Verifica si el timer está caducado para emitir eventos de actualización.
       * El sonido no se gestiona aquí; se reproduce solo en los watchers de cambio de estado.
       */
      checkTimerExpiration() {
          if (!this.timer) return
          if (this.isAvailable || (this.timer.isManual && this.manualNeedsAction)) {
              this.$emit('updateTimer', false)
          }
      },

      handleManualStateChange() {
        // Notificar al componente padre para actualizar el ordenamiento
        this.$emit('updateTimer', true)
      },
    updateManualPc(pct) {
        if (this.manualNeedsAction) {
            this.manual_pc = 100
            return
        }

        if (this.manualRunning) {
            const clamped = Math.max(0, Math.min(100, Math.round(pct)))
            this.manual_pc = clamped
            return
        }

        const clamped = Math.max(0, Math.min(100, Math.round(pct)))
        this.manual_pc = clamped
    },
    handleManualNeedsAction(needsAction) {
        this.manualNeedsAction = needsAction
        if (needsAction) {
            this.manual_pc = 100
        }
        this.$emit('updateTimer', true)
    },
    handleManualFinished() {
        this.manual_pc = 100
        this.manualRunning = false
        this.manualNeedsAction = true
        this.$emit('updateTimer', true)
    },
    handleManualStopped() {
        this.manualRunning = false
        this.manual_pc = 0
        this.manualNeedsAction = false
    },

    handleManualReset() {
        this.manualRunning = false
        this.manual_pc = 0
        this.manualNeedsAction = false
        this.$emit('updateTimer', true)
    },
    handleTimeStatusChange(isMoreThanSixHours) {
        const self = this
        if (isMoreThanSixHours && !this.showAll) {
            self.$store.dispatch('timer/setHideAlarm', { alarmId: self.timer.id })
        }
    },
    setAlarmAsDone() {
        const self = this
        const ts = self.clickTimestamp || Date.now()

        self.$store.dispatch('timer/setAlarmAsDone', {
            alarm: self.timer,
            checkDateTimestamp: ts
        })

        if (self.timer && self.timer.isManual && self.$refs.manualTimer) {
            self.$refs.manualTimer.markAsCompleted()
        }

        self.manualNeedsAction = self.manualRunning = false
        self.manual_pc = 0

        self.$emit('updateTimer', true)
    },
        handleTimerClick() {
        const self = this
        const timestamp = Date.now()
        self.clickTimestamp = timestamp

        // Para timers manuales que han terminado y necesitan acción
        if (self.timer.isManual && self.manualNeedsAction) {
            // Usar la misma lógica que los timers normales para determinar si es firma o check
            if (self.timer.type == 2) {
                const kitchenMode = localStorage.kitchenMode == null || localStorage.kitchenMode === 'true'
                const userLogged = this.$store.getters['loginUser/getLocalEmployee']

                if (kitchenMode && !userLogged) {
                    this.$popup.confirm({
                        message: this.$t('timers.popup_sign.title', { timer: this.timer.name }),
                        textSave: this.$t('timers.popup_sign.confirm'),
                        textCancel: this.$t('timers.popup_sign.cancel'),
                        autoClose: false,
                        callSave: async function (self2, store) {
                            self.$store.commit('loginUser/setRefreshLayout', false)
                            await self.$popup.close()
                            self.$popup.login({
                                list: store.getters['employee/getLoginUsers'](0),
                                callAfterAccess: function () {
                                    self.setManualAlarmAsDone()
                                    self.$popup.close()
                                    self.$store.commit('loginUser/setRefreshLayout', true)
                                }
                            })
                        }
                    })
                } else {
                    if (!self.userAppAccess) {
                        this.$popup.alert({ message: this.$t('answer.no_access_app_sign', { name: self.user.name }) })
                    } else {
                        this.$popup.confirm({
                            message: this.$t('timers.popup_sign.title', { timer: this.timer.name }),
                            textSave: this.$t('timers.popup_sign.confirm'),
                            textCancel: this.$t('timers.popup_sign.cancel'),
                            callSave: () => self.setManualAlarmAsDone()
                        })
                    }
                }
            } else {
                // Timer tipo 1 (check)
                self.setManualAlarmAsDone()
            }
            return
        }

        // Resto de la lógica original para timers normales...
        if (self.timer.type == 2) {
            const kitchenMode = localStorage.kitchenMode == null || localStorage.kitchenMode === 'true'
            const userLogged = this.$store.getters['loginUser/getLocalEmployee']

            if (kitchenMode && !userLogged) {
                this.$popup.confirm({
                    message: this.$t('timers.popup_sign.title', { timer: this.timer.name }),
                    textSave: this.$t('timers.popup_sign.confirm'),
                    textCancel: this.$t('timers.popup_sign.cancel'),
                    autoClose: false,
                    callSave: async function (self2, store) {
                        self.$store.commit('loginUser/setRefreshLayout', false)
                        await self.$popup.close()
                        self.$popup.login({
                            list: store.getters['employee/getLoginUsers'](0),
                            callAfterAccess: function () {
                                self.setAlarmAsDone()
                                self.$popup.close()
                                self.$store.commit('loginUser/setRefreshLayout', true)
                            }
                        })
                    }
                })
            } else {
                const self = this
                if (!self.userAppAccess) {
                    this.$popup.alert({ message: this.$t('answer.no_access_app_sign', { name: self.user.name }) })
                } else {
                    this.$popup.confirm({
                        message: this.$t('timers.popup_sign.title', { timer: this.timer.name }),
                        textSave: this.$t('timers.popup_sign.confirm'),
                        textCancel: this.$t('timers.popup_sign.cancel'),
                        callSave: this.setAlarmAsDone
                    })
                }
            }
        } else {
            this.setAlarmAsDone()
        }

        this.$emit('updateTimer', true)
    },

    async setManualAlarmAsDone() {
    const self = this

    // Primero ejecutar el CHECK en el servidor
    try {
      const result = await timerSyncService.checkTimer(self.timer.id)

      if (result && result.success) {
        // Si el servidor confirma, entonces actualizar el store local
        self.$store.dispatch('timer/setAlarmAsDone', {
          alarm: self.timer,
          checkDateTimestamp: self.clickTimestamp
        }).then(() => {
          // Marcar el timer manual como completado en el componente
          if (self.$refs.manualTimer) {
            self.$refs.manualTimer.markAsCompleted()
          }

          // Resetear las variables locales
          self.manualRunning = false
          self.manual_pc = 100 // Mantener lleno para mostrar completado
          self.manualNeedsAction = false

          // Emitir actualización del timer
          self.$emit('updateTimer', true)
        })
      } else {
        self.$popup.alert({ message: 'No se pudo confirmar la firma en el servidor' })
      }
    } catch (error) {
      self.$popup.alert({ message: 'Error al procesar la firma' })
    }
  },
        updateWindowWidth() {
            this.windowWidth = window.innerWidth
        }
    },
    mounted() {
        window.addEventListener('resize', this.updateWindowWidth)
        this.$store.dispatch('timer/loadManualTimersState')

        // Si el timer ya está disponible al montar, el watcher isAvailable no detecta
        // la transición (arranca en true). Se reproduce el sonido una sola vez aquí.
        this.$nextTick(() => {
            if (this.isAvailable && this.timer && this.soundEnabled) {
                soundService.playNotificationSound('/sounds/timer-expired-alert.mp3', 1.0, `operational-timer-${this.timer.id}`)
            }
        })

        this.expirationCheckInterval = setInterval(() => {
            this.checkTimerExpiration()
        }, 10000)

        this.uiTickInterval = setInterval(() => {
            this.uiTick++
        }, 1000)
    },
    beforeDestroy() {
        this.clickTimestamp = null
        window.removeEventListener('resize', this.updateWindowWidth)
        if (this.expirationCheckInterval) {
            clearInterval(this.expirationCheckInterval)
        }
        if (this.uiTickInterval) {
            clearInterval(this.uiTickInterval)
        }
    }
}
</script>

<style lang="scss" scoped>
.maximum-six-hours-hide {
    display: none !important;
}

.maximum-six-hours {
    display: block !important;
}

.timer-box {
    border-radius: 4px;
    box-sizing: border-box;
    padding: 24px;
    display: grid;
    grid-template-columns: 1fr 120px;
    min-height: 96px;
    max-height: fit-content;
    position: relative;

    h2 {
        margin-block-start: 0;
        margin-block-end: 0;
        padding: 0;
    }

    p {
        padding: 0;
    }

    .icon-container {
        height: 100%;
        // height: 55px;
        display: flex;
        align-items: center;
        justify-content: center;

        .icon {
            width: 20px;
            height: 20px;
            object-fit: contain;
            opacity: 0.25;
        }

        @media (max-width: 768px) {
            .icon {
                width: 20px;
                height: 20px;
                object-fit: contain;
                margin-top: 16px;
            }
        }
    }

    .text-container {
        display: flex;
        flex-direction: column;
        padding: 0 8px;
        overflow: hidden;
        justify-content: center;

        width: 100%;
        max-width: 100%;
        height: 100%;
        max-height: 100%;
    }

    .timer-title {
        font-size: 24px;
        // height: 36px;
        text-transform: uppercase;

        width: 100%;
        max-width: 100%;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;

        @include text-ellipsis($line: 3);

        // white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: $color-black;
    }

    .timer-text {
        font-family: $text;
        font-size: 16px;
        // line-height: normal;
        line-height: 130%;
        font-weight: 400;

        &.bold {
            font-family: $text-bold;
        }
    }

    .timer-description {
        padding-top: 4px;
        width: 100%;

        @include text-ellipsis($line: 2);
    }

    .time {
        padding-top: 8px;

        .timer-text {
            font-weight: 500;
        }
    }

    .timer-countdown-container {
        display: grid;
        place-items: center;
        &.hour {
            background-size: 90px;
        }
        &.action {
            cursor: pointer;
            background-repeat: no-repeat;
            background-size: 70px;
            background-position: center;
            color: white;

            &.type1 {
                background-image: img('circle_play.svg');
            }
            &.type2 {
                background-image: img('icon-sign.svg');
                // background-image: img('circle_check.svg');
            }
        }
    }

    .graph.chart {
        height: 90px;
        width: 90px;
        padding: 0;
        // margin: 0px !important;

        .c100 {
            position: relative;
            // width: 3.3625rem;
            // height: 3.3625rem;
            width: 100%;
            height: 100%;
            // margin: 0px !important;

            .bar,
            .fill,
            &::after {
                // width: calc(3.3625rem - 1.125rem);
                // height: calc(3.3625rem - 1.125rem);
                width: calc(90px - calc(70px * 0.21));
                height: calc(90px - calc(70px * 0.21));
            }
        }
    }

    @media (min-width: 901px) and (max-width: 1024px) {
        .graph.chart {
            .c100 {
                .bar,
                .fill,
                &::after {
                    width: calc(90px - calc(70px * 0.18));
                    height: calc(90px - calc(70px * 0.18));
                }
            }
        }
    }

    @media (min-width: 1025px) {
        // height: 160px;
        min-height: 175px;
        max-height: max-content;
        grid-template-columns: 1fr 120px;
        // padding-top: 16px;

        .text-container {
            width: 100%;
            height: 100%;
            overflow: hidden;

            .timer-title {
                font-size: 24px;
                line-height: 28px;
            }

            .timer-text {
                // line-height: 150%;
                font-size: 14px;
                line-height: 14px;
            }
        }

        &.available {
            .text-container {
                .timer-title{
                    font-size: 28px;
                    line-height: 30px;

                }
            }
        }

        .icon {
            width: 25px;
            min-width: 25px;
            height: 25px;
            min-height: 25px;
        }

        .timer-countdown-container.action {
            background-size: 100px;
        }

        .graph.chart {
            width: 120px;
            height: 120px;

            .c100 {
                // width: 100px;
                // height: 100px;
                width: 120px;
                height: 120px;

                // span {
                //     font-size: 24px;
                //     line-height: 5.5rem;
                // }

                // .bar,
                // .fill,
                // &::after {
                //     width: calc(5.3625rem - 1.125rem);
                //     height: calc(5.3625rem - 1.125rem);
                // }

                .bar,
                .fill,
                &::after {
                    width: calc(120px - calc(100px * 0.18));
                    height: calc(120px - calc(100px * 0.18));

                    // width: calc(5.3625rem - 1.125rem);
                    // height: calc(5.3625rem - 1.125rem);
                }
            }
        }
    }
}

.available {
    background-color: $color-error-500;
    background: linear-gradient(311deg, $color-error-500, #ce3d2b);
    background-size: 400% 400%;

    -webkit-animation: bgGradient 3s ease infinite, card-pulse 1000ms cubic-bezier(0.4, 0, 0.6, 1) infinite;
    -moz-animation: bgGradient 3s ease infinite, card-pulse 1000ms cubic-bezier(0.4, 0, 0.6, 1) infinite;
    animation: bgGradient 3s ease infinite, card-pulse 1000ms cubic-bezier(0.4, 0, 0.6, 1) infinite;

    .timer-title {
        color: $color-white;
    }
    .timer-description {
        display: none;
    }

    .timer-text {
        color: $color-white;
    }

    .icon {
        filter: invert(100%) sepia(0%) saturate(7486%) hue-rotate(204deg) brightness(112%) contrast(100%);
        filter: invert(12%) sepia(34%) saturate(3520%) hue-rotate(349deg) brightness(87%) contrast(116%);
        opacity: 0.5 !important;
    }
    .corner-dot{
        border: 0.14rem solid rgba(255, 255, 255, 0.8);
    }
}

.not-available {
    background-color: $color-white;

    .timer-text {
        color: $color-neutral-600;
    }
}

.corner-dot {
    position: absolute;
    width: 16px; /* Tamaño del círculo */
    height: 16px; /* Tamaño del círculo */
    background-color: #ff0000; /* Color del círculo */
    border-radius: 50%; /* Hace que el punto sea redondo */
    margin-right: 1.125rem;
    top: 4px;
    left: 4px;
}

/* duplicate removed */

@-webkit-keyframes bgGradient {
    0% {
        background-position: 9% 0%;
    }
    50% {
        background-position: 92% 100%;
    }
    100% {
        background-position: 9% 0%;
    }
}
@-moz-keyframes bgGradient {
    0% {
        background-position: 9% 0%;
    }
    50% {
        background-position: 92% 100%;
    }
    100% {
        background-position: 9% 0%;
    }
}
@keyframes bgGradient {
    0% {
        background-position: 9% 0%;
    }
    50% {
        background-position: 92% 100%;
    }
    100% {
        background-position: 9% 0%;
    }
}
</style>
